import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from '@/components/ui/dialog';
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Copy, Wallet } from 'lucide-react';
import { productsAPI, ordersAPI, walletAPI } from '@/services/api';

type Product = {
    id: string; title: string; description: string;
    price: number; price_annual: string | null; agent_commission_pct: string;
};

type WalletInfo = {
    balance?: number;
    available?: number;
    currency?: string;
};

interface Props {
    onCreated: () => void;
}

const fa = new Intl.NumberFormat('fa-IR');

const CreateOrderModal: React.FC<Props> = ({ onCreated }) => {
    const [open, setOpen] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [productId, setProductId] = useState<string>('');
    const [quantity, setQuantity] = useState<number>(1);
    const [wallet, setWallet] = useState<WalletInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [loadingInit, setLoadingInit] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<null | { order:any; invoice:any; licenses:any[] }>(null);

    useEffect(() => {
        if (!open) return;
        setLoadingInit(true);
        Promise.all([
            productsAPI.getAgentProducts(),
            walletAPI.getWalletInfo().catch(() => null)
        ])
            .then(([prods, wal]) => {
                setProducts(prods);
                setWallet(wal);
            })
            .catch(() => setError('خطا در بارگذاری اطلاعات اولیه'))
            .finally(() => setLoadingInit(false));
    }, [open]);

    const selectedProduct = useMemo(
        () => products.find((p) => p.id === productId) || null,
        [products, productId]
    );

    const unitPrice = selectedProduct?.price ?? 0;
    const totalAmount = useMemo(() => unitPrice * (quantity > 0 ? quantity : 0), [unitPrice, quantity]);
    const walletBalance = wallet?.balance ?? wallet?.available ?? 0;
    const hasEnoughBalance = totalAmount <= walletBalance || totalAmount === 0;

    const handleCreate = async () => {
        setError('');
        if (!productId) return setError('لطفاً محصول را انتخاب کنید');
        if (quantity < 1) return setError('تعداد باید حداقل ۱ باشد');
        if (!hasEnoughBalance) return setError('موجودی کیف‌پول کافی نیست');

        setLoading(true);
        try {
            const payload = { productId: Number(productId), quantity };
            const data = await ordersAPI.createOrder(payload);
            setResult(data);
            onCreated();
        } catch {
            setError('ثبت سفارش ناموفق بود');
        } finally {
            setLoading(false);
        }
    };

    const copyKeys = () => {
        if (!result) return;
        const text = result.licenses.map(l => l.license_key).join('\n');
        navigator.clipboard.writeText(text);
    };

    const goToWallet = () => window.location.href = '/wallet';

    return (
        <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if(!v) setResult(null); }}>
            <DialogTrigger asChild>
                <Button className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-5 py-2">
                    ایجاد سفارش
                </Button>
            </DialogTrigger>

            <DialogContent dir="rtl" className="sm:max-w-lg rounded-2xl p-6">
                <DialogHeader className="text-right border-b pb-3 mb-4">
                    <DialogTitle className="text-xl font-semibold text-gray-800">ایجاد سفارش جدید</DialogTitle>
                </DialogHeader>

                {/* موجودی */}
                <div className="flex items-center justify-between rounded-xl border border-green-200 bg-green-50 px-4 py-2 mb-4">
                    <div className="flex items-center gap-2 text-green-700">
                        <Wallet className="h-5 w-5" />
                        <span className="text-sm">موجودی کیف‌پول:</span>
                    </div>
                    <Badge variant="outline" className="border-green-400 text-green-700 text-base font-medium">
                        {loadingInit ? '...' : `${fa.format(walletBalance/10)} تومان`}
                    </Badge>
                </div>

                {error && (
                    <Alert variant="destructive" className="mb-4">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {!result ? (
                    <div className="space-y-5">
                        {/* انتخاب محصول */}
                        <div className="grid gap-2">
                            <Label className="text-right text-gray-700">محصول</Label>
                            <Select
                                disabled={loadingInit || loading}
                                value={productId}
                                onValueChange={setProductId}
                            >
                                <SelectTrigger className="rounded-lg">
                                    <SelectValue placeholder={loadingInit ? 'در حال بارگذاری…' : 'انتخاب محصول'} />
                                </SelectTrigger>
                                <SelectContent>
                                    {products.map(p => (
                                        <SelectItem key={p.id} value={p.id}>
                                            <div className="flex items-center justify-between gap-3 w-full">
                                                <span>{p.title}</span>
                                                <Badge variant="outline" className="border-green-300 text-green-600">
                                                    {fa.format(p.price/10)} تومان
                                                </Badge>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* تعداد */}
                        <div className="grid gap-2">
                            <Label className="text-right text-gray-700">تعداد لایسنس</Label>
                            <Input
                                type="number"
                                min={1}
                                value={quantity}
                                onChange={e => setQuantity(Math.max(1, parseInt(e.target.value || '1', 10)))}
                                dir="ltr"
                                className="rounded-lg border-gray-300"
                            />
                        </div>

                        {/* جمع مبلغ */}
                        <div className="bg-gray-50 rounded-xl p-3 text-sm space-y-2">
                            <div className="flex justify-between text-gray-600">
                                <span>مبلغ هر واحد:</span>
                                <span className="font-medium">{fa.format(unitPrice/10)} تومان</span>
                            </div>
                            <div className="flex justify-between font-semibold text-gray-800">
                                <span>جمع کل:</span>
                                <span className="text-green-600">{fa.format(totalAmount/10)} تومان</span>
                            </div>
                        </div>

                        {!hasEnoughBalance && (
                            <Alert variant="destructive" className="rounded-xl border-red-300 bg-red-50">
                                <AlertDescription>
                                    موجودی شما برای این سفارش کافی نیست. لطفاً ابتدا کیف‌پول را شارژ کنید.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                ) : (
                    <div className="space-y-4 text-right">
                        <div className="space-y-1">
                            <div className="text-sm text-gray-600">سفارش #{result.order.id}</div>
                            <div>مبلغ کل: <b>{fa.format(result.order.total_price || result.invoice?.price || 0)} تومان</b></div>
                            <div>وضعیت: <Badge className="bg-green-600 text-white">{result.order.status}</Badge></div>
                        </div>

                        <div className="space-y-2">
                            <div className="font-medium text-gray-800">لایسنس‌های صادر شده ({fa.format(result.licenses.length)}):</div>
                            <div className="max-h-40 overflow-auto rounded-md border border-gray-200 p-2 text-left" dir="ltr">
                                {result.licenses.map((l) => (
                                    <div key={l.id} className="py-0.5">{l.license_key}</div>
                                ))}
                            </div>
                            <Button variant="outline" size="sm" onClick={copyKeys} className="border-green-400 text-green-600 hover:bg-green-100">
                                <Copy className="ml-2 h-4 w-4" /> کپی همه لایسنس‌ها
                            </Button>
                        </div>
                    </div>
                )}

                <DialogFooter className="mt-6 flex items-center justify-between">
                    {!result ? (
                        <>
                            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-xl">
                                انصراف
                            </Button>
                            <div className="flex gap-2">
                                {!hasEnoughBalance && (
                                    <Button variant="outline" onClick={goToWallet} className="border-green-400 text-green-600 hover:bg-green-100 rounded-xl">
                                        شارژ کیف‌پول
                                    </Button>
                                )}
                                <Button
                                    onClick={handleCreate}
                                    disabled={loadingInit || loading || !hasEnoughBalance || !productId}
                                    className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-6"
                                >
                                    {loading && <Loader2 className="ml-2 h-4 w-4 animate-spin" />}
                                    ثبت سفارش
                                </Button>
                            </div>
                        </>
                    ) : (
                        <Button onClick={()=>setOpen(false)} className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-6">
                            بستن
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CreateOrderModal;
